import type { Components } from 'react-markdown';

/**
 * Markdown styling for `.md` previews and image OCR text. Kept at module scope — a new `components`
 * object remounts every node and loses highlights.
 */
export const documentMarkdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-theme-black mt-6 mb-4 text-xl font-bold first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-theme-black mt-5 mb-3 text-lg font-bold first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-theme-black mt-4 mb-2 text-base font-bold first:mt-0">{children}</h3>,
  h4: ({ children }) => <h4 className="text-theme-black mt-3 mb-2 text-sm font-bold first:mt-0">{children}</h4>,
  ul: ({ children }) => <ul className="text-theme-black mb-3 list-disc pl-5 leading-6">{children}</ul>,
  ol: ({ children, start }) => (
    <ol start={start} className="text-theme-black mb-3 list-decimal pl-5 leading-6">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="text-theme-black mb-1">{children}</li>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="font-medium text-blue-600 transition-colors hover:text-blue-800 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  p: ({ children }) => <p className="text-theme-black mb-3 leading-6 break-words last:mb-0">{children}</p>,
  pre: ({ children }) => (
    <pre className="border-theme-border-grey bg-theme-background-grey text-theme-black mb-3 block overflow-x-auto rounded-md border p-3 text-sm">
      {children}
    </pre>
  ),
  code: ({ children, ...props }) => (
    <code className="bg-theme-background-grey text-theme-black rounded px-1 py-0.5 text-sm" {...props}>
      {children}
    </code>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-theme-mid-grey bg-theme-background-grey text-theme-text-grey my-3 rounded-r-sm border-l-4 py-1 pl-4 italic">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="mb-3 overflow-x-auto">
      <table className="border-theme-background-grey w-full border-collapse border text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-theme-background-grey">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-theme-background-grey bg-theme-white divide-y">{children}</tbody>,
  th: ({ children }) => (
    <th className="border-theme-background-grey border px-3 py-2 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border-theme-background-grey border px-3 py-2 align-top">{children}</td>,
  hr: () => <hr className="border-theme-border-grey my-4" />,
};
